/**
 * Schedule API 함수들
 *
 * 시간 기반 가격 조정 규칙 관리 (Item, ItemGroup, User)
 */

import { AuthContextType } from "../features/auth/AuthContext";
import { apiWithTimeout } from "../shared/api/timeout";
import { apiHost, isPayloadError, getErrorMessage } from "../shared/api/helpers";
import { handleDRFErrors } from "../shared/lib/errorHandler";
import { UseFormSetError } from "react-hook-form";

export interface BaseSchedulePayload {
    time_start: string;
    time_end: string;
    is_mon: boolean;
    is_tue: boolean;
    is_wed: boolean;
    is_thu: boolean;
    is_fri: boolean;
    is_sat: boolean;
    is_sun: boolean;
    price_ll_value: number;
    price_ll_value_type: string;
    price_default_value: number;
    price_default_value_type: string;
    is_enabled: boolean;
}

export interface ItemSchedulePayload extends BaseSchedulePayload {
    item: string;
}

export interface ItemGroupSchedulePayload extends BaseSchedulePayload {
    itemgroup: string;
}

export interface UserSchedulePayload extends BaseSchedulePayload {
}

export interface CreateScheduleOpts<T extends BaseSchedulePayload> {
    auth: AuthContextType,
    payload: T,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
    setError: UseFormSetError<any>
}

export async function fetchItemSchedules(opts: {
    auth: AuthContextType,
    itemId: string,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { auth, itemId, setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/schedules/items/?item=${itemId}`);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function fetchItemGroupSchedules(opts: {
    auth: AuthContextType,
    itemGroupId: string,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { auth, itemGroupId, setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/schedules/itemgroups/?itemgroup=${itemGroupId}`);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function fetchUserSchedules(opts: {
    auth: AuthContextType,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { auth, setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/schedules/user/`);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function createItemSchedule(opts: CreateScheduleOpts<ItemSchedulePayload>) {
    const { payload, auth, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        setErrorMessage && setErrorMessage(null);
        setProcessMessage && setProcessMessage("시간 규칙을 저장 중입니다...");
        await apiWithTimeout.normal.post(`${apiHost}/api/schedules/items/`, payload);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        const errorData = error.response?.data;
        handleDRFErrors(errorData, setError, setErrorMessage)
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function createItemGroupSchedule(opts: CreateScheduleOpts<ItemGroupSchedulePayload>) {
    const { payload, auth, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        setErrorMessage && setErrorMessage(null);
        setProcessMessage && setProcessMessage("시간 규칙을 저장 중입니다...");
        await apiWithTimeout.normal.post(`${apiHost}/api/schedules/itemgroups/`, payload);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        const errorData = error.response?.data;
        handleDRFErrors(errorData, setError, setErrorMessage)
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function createUserSchedule(opts: CreateScheduleOpts<UserSchedulePayload>) {
    const { payload, auth, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        setErrorMessage && setErrorMessage(null);
        setProcessMessage && setProcessMessage("시간 규칙을 저장 중입니다...");
        await apiWithTimeout.normal.post(`${apiHost}/api/schedules/user/`, payload);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        const errorData = error.response?.data;
        handleDRFErrors(errorData, setError, setErrorMessage)
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function retrieveItemSchedule(opts: {
    auth: AuthContextType,
    id: string,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { auth, id, setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/schedules/items/${id}/`);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function retrieveItemGroupSchedule(opts: {
    auth: AuthContextType,
    id: string,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { auth, id, setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/schedules/itemgroups/${id}/`);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function retrieveUserSchedule(opts: {
    auth: AuthContextType,
    id: string,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { auth, id, setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/schedules/user/${id}/`);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function updateItemSchedule(opts: {
    auth: AuthContextType,
    id: string,
    payload: any,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
    setError?: (...args: any) => void;
}) {
    const { auth, id, payload, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        setErrorMessage && setErrorMessage(null);
        setProcessMessage && setProcessMessage("시간 규칙을 업데이트 중입니다...");
        await apiWithTimeout.normal.patch(`${apiHost}/api/schedules/items/${id}/`, payload);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        const errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        const errorData = error.response?.data;

        if (errorData?.errors && setError) {
            for (const [key, value] of Object.entries(errorData.errors)) {
                setError(key, { type: "custom", message: value });
            }
        } else if (isPayloadError(errorData, payload) && setError) {
            for (const [key, value] of Object.entries(errorData)) {
                setError(key, { type: "custom", message: value });
            }
        } else if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function updateItemGroupSchedule(opts: {
    auth: AuthContextType,
    id: string,
    payload: any,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
    setError?: (...args: any) => void;
}) {
    const { auth, id, payload, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        setErrorMessage && setErrorMessage(null);
        setProcessMessage && setProcessMessage("시간 규칙을 업데이트 중입니다...");
        await apiWithTimeout.normal.patch(`${apiHost}/api/schedules/itemgroups/${id}/`, payload);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        const errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        const errorData = error.response?.data;

        if (errorData?.errors && setError) {
            for (const [key, value] of Object.entries(errorData.errors)) {
                setError(key, { type: "custom", message: value });
            }
        } else if (isPayloadError(errorData, payload) && setError) {
            for (const [key, value] of Object.entries(errorData)) {
                setError(key, { type: "custom", message: value });
            }
        } else if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function updateUserSchedule(opts: {
    auth: AuthContextType,
    id: string,
    payload: any,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
    setError?: (...args: any) => void;
}) {
    const { auth, id, payload, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        setErrorMessage && setErrorMessage(null);
        setProcessMessage && setProcessMessage("시간 규칙을 업데이트 중입니다...");
        await apiWithTimeout.normal.patch(`${apiHost}/api/schedules/user/${id}/`, payload);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        const errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        const errorData = error.response?.data;

        if (errorData?.errors && setError) {
            for (const [key, value] of Object.entries(errorData.errors)) {
                setError(key, { type: "custom", message: value });
            }
        } else if (isPayloadError(errorData, payload) && setError) {
            for (const [key, value] of Object.entries(errorData)) {
                setError(key, { type: "custom", message: value });
            }
        } else if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function deleteItemSchedule(opts: {
    auth: AuthContextType,
    id: string,
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setResult?: () => void;
}) {
    const { auth, id, setErrorMessage, setProcessMessage, setResult } = opts;

    try {
        await apiWithTimeout.normal.delete(`${apiHost}/api/schedules/items/${id}/`);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        let errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function deleteItemGroupSchedule(opts: {
    auth: AuthContextType,
    id: string,
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setResult?: () => void;
}) {
    const { auth, id, setErrorMessage, setProcessMessage, setResult } = opts;

    try {
        await apiWithTimeout.normal.delete(`${apiHost}/api/schedules/itemgroups/${id}/`);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        let errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function deleteUserSchedule(opts: {
    auth: AuthContextType,
    id: string,
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setResult?: () => void;
}) {
    const { auth, id, setErrorMessage, setProcessMessage, setResult } = opts;

    try {
        await apiWithTimeout.normal.delete(`${apiHost}/api/schedules/user/${id}/`);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        let errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function resolveSchedule(opts: {
    auth: AuthContextType,
    itemId: string,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { auth, itemId, setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/schedules/resolve/?item_id=${itemId}`);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        if (error.response?.status === 404) {
            if (error.response.data.message == "No active schedule found") {
                return
            }
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}
